import React from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from 'shared/components/ui/button';
import { Sizes, Variants } from 'shared/components/ui/button/types';

import * as Styled from '../event-form.style';
import { EventRow } from '../event-row';

export type EventVideoCallRowProps = {
  url: string;
  // the editable form carries the icon on the toggle row above this one, so it passes none
  icon?: React.FC;
  // the provider's own label for the conference ("Zoom Meeting"); absent when it ships none
  providerName?: string | null;
  // the editable form keeps the raw link instead — that is what you copy out of a form
  showJoinButton?: boolean;
  // present only when swapping this conference for a Meet one is possible AND meaningful — i.e. a
  // Google event whose conference is not already Meet. Omitted, the row is read-only as before.
  onReplace?: () => void;
  // the swap is armed and happens on save; the link below is the one still on the event
  isReplacePending?: boolean;
};

export const EventVideoCallRow: React.FC<EventVideoCallRowProps> = ({
  url,
  icon,
  providerName,
  showJoinButton,
  onReplace,
  isReplacePending,
}) => {
  const { t } = useTranslation();

  const onJoin = () => window.open(url, '_blank', 'noreferrer');

  return (
    <EventRow icon={icon} alignTop>
      <Styled.VideoCallValue>
        {!!providerName && (
          <Styled.VideoCallProvider>{providerName}</Styled.VideoCallProvider>
        )}
        {showJoinButton ? (
          <Styled.VideoCallAction>
            <Button
              type="button"
              variant={Variants.Contained}
              size={Sizes.Small}
              onClick={onJoin}
            >
              {t('forms.event.fields.video-call.join')}
            </Button>
          </Styled.VideoCallAction>
        ) : (
          <Styled.ReadOnlyValue>
            <a href={url} target="_blank" rel="noreferrer">
              {url}
            </a>
          </Styled.ReadOnlyValue>
        )}
        {isReplacePending && (
          <Styled.VideoCallNote>
            {t('forms.event.fields.video-call.replace-pending')}
          </Styled.VideoCallNote>
        )}
        {!!onReplace && !isReplacePending && (
          <Styled.VideoCallAction>
            <Button
              type="button"
              variant={Variants.Text}
              size={Sizes.Small}
              onClick={onReplace}
            >
              {t('forms.event.fields.video-call.replace')}
            </Button>
          </Styled.VideoCallAction>
        )}
      </Styled.VideoCallValue>
    </EventRow>
  );
};
